import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Button, 
  TextField, 
  FormControl, 
  Stack,
  Avatar,
  Divider,
  IconButton,
  Grid,
  Paper
} from '@mui/material';
import { 
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API } from '../API/api';
import SeparatorBreadcrumbs from '../Components/SeparatorBreadcrumbs';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedAuthState = sessionStorage.getItem('authLocalstorage');
    const parsed = JSON.parse(storedAuthState);

    if (parsed && parsed.token) {
      fetchUserProfile();
    } else {
      setError("Authentication information missing");
      setIsLoading(true);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const stored = sessionStorage.getItem('authLocalstorage');
      const parsed = JSON.parse(stored);

      const response = await axios.get(`${API}/users/${parsed.user.id}`, {
        headers: { Authorization: `Bearer ${parsed.token}` }
      });

      if (response.data.data) {
        setUser(response.data.data);
        setFormData({
          name: response.data.data.name,
          email: response.data.data.email,
          password: '',
          password_confirmation: ''
        });
      } else {
        setError("No user data received");
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const updateData = {
      name: formData.name,
      email: formData.email,
      ...(formData.password && {
        password: formData.password,
        password_confirmation: formData.password_confirmation
      })
    };

    const stored = sessionStorage.getItem('authLocalstorage');
    const parsed = JSON.parse(stored);

    try {
      const response = await axios.put(
        `${API}/users/${parsed.user.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${parsed.token}` } }
      );
      setSuccess('Profile updated successfully');
      setUser(response.data.data);
      setIsEditing(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <SeparatorBreadcrumbs 
          items={[
            { label: 'Home', path: '/dashboard' },
            { label: 'Profile', path: '/profile' }
          ]} 
        />
      </Box>

      <Grid container spacing={3}>
        {/* LEFT CARD */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 3,
              height: '100%',
              borderRadius: "23px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 130,
                  height: 130,
                  bgcolor: 'primary.main',
                  fontSize: '3.2rem',
                  mb: 2,
                  borderRadius: "23px",
                  boxShadow: 4
                }}
              >
                {formData.name?.charAt(0)?.toUpperCase()}
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {formData.name}
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formData.email}
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* RIGHT FORM */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              p: 3,
              borderRadius: "23px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.07)"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700 }}>
                Profile Settings
              </Typography>

              {!isEditing && (
                <IconButton
                  color="primary"
                  onClick={() => setIsEditing(true)}
                  sx={{
                    borderRadius: "23px",
                    bgcolor: "primary.light",
                    "&:hover": { bgcolor: "primary.main", color: "white" }
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            {error && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: "23px",
                  bgcolor: "error.light",
                  color: "error.dark"
                }}
              >
                <Typography>{error}</Typography>
              </Paper>
            )}

            {success && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: "23px",
                  bgcolor: "success.light",
                  color: "success.dark"
                }}
              >
                <Typography>{success}</Typography>
              </Paper>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* NAME */}
                <FormControl>
                  <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                    <PersonIcon sx={{ color: "primary.main", mr: 1 }} />
                    <TextField
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      fullWidth
                      variant="standard"
                      sx={{ borderRadius: "23px" }}
                    />
                  </Box>
                </FormControl>

                {/* EMAIL */}
                <FormControl>
                  <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                    <EmailIcon sx={{ color: "primary.main", mr: 1 }} />
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      fullWidth
                      variant="standard"
                      sx={{ borderRadius: "23px" }}
                    />
                  </Box>
                </FormControl>

                {/* PASSWORD SECTION */}
                {isEditing && (
                  <>
                    <Divider sx={{ my: 1 }}>
                      <Typography color="text.secondary">Change Password</Typography>
                    </Divider>

                    <FormControl>
                      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                        <LockIcon sx={{ color: "primary.main", mr: 1 }} />
                        <TextField
                          label="New Password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          fullWidth
                          variant="standard"
                          sx={{ borderRadius: "23px" }}
                        />
                      </Box>
                    </FormControl>

                    <FormControl>
                      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                        <LockIcon sx={{ color: "primary.main", mr: 1 }} />
                        <TextField
                          label="Confirm Password"
                          name="password_confirmation"
                          type="password"
                          value={formData.password_confirmation}
                          onChange={handleInputChange}
                          fullWidth
                          variant="standard"
                          sx={{ borderRadius: "23px" }}
                        />
                      </Box>
                    </FormControl>
                  </>
                )}

                {/* BUTTONS */}
                {isEditing && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      sx={{ borderRadius: "23px", px: 3 }}
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          name: user.name,
                          email: user.email,
                          password: '',
                          password_confirmation: ''
                        });
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      sx={{ borderRadius: "23px", px: 3 }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Stack>
            </form>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
